import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Activity, Calendar, User } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Modules de l'application : chaque type d'action est rattaché à l'un
// d'entre eux pour afficher une étiquette colorée dans l'historique.
const MODULES = {
  main_courante: { label: 'Main Courante', color: '#2563EB' },
  consignes: { label: 'Consignes', color: '#D97706' },
  organigramme: { label: 'Organigramme', color: '#7C3AED' },
  annuaire: { label: 'Annuaire', color: '#0D9488' },
  sites: { label: 'Sites', color: '#4F46E5' },
  utilisateurs: { label: 'Utilisateurs', color: '#E11D48' },
  permissions: { label: 'Permissions', color: '#DC2626' },
  calendrier: { label: 'Calendrier', color: '#0891B2' },
  reporting: { label: 'Reporting', color: '#16A34A' },
  messagerie: { label: 'Messagerie', color: '#DB2777' },
  rapports: { label: 'Rapports agents', color: '#EA580C' },
  compte: { label: 'Compte', color: '#64748B' },
  autre: { label: 'Autre', color: '#94A3B8' },
};

// Libellé en français lisible pour chaque type d'action précis (à défaut,
// le type brut est affiché tel quel).
const ACTION_LABELS = {
  event_marked_important: 'Événement marqué important',
  event_observation_added: 'Observation ajoutée à un événement',
  event_corrected: 'Événement corrigé',
  event_validated: 'Événement validé (saisie tardive)',
  key_return_acknowledged: 'Retour de clé acquitté (omission de saisie)',
  key_alert_validated: "Avertissement de clé non rendue validé",
  data_access: 'Consultation de données',
  agent_control_created: 'Fiche de contrôle créée',
  agent_report_created: 'Rapport agent créé',
  agent_report_edited: 'Rapport agent modifié',
  agent_report_deleted: 'Rapport agent supprimé',
  announcement_created: 'Annonce créée',
  announcement_updated: 'Annonce modifiée',
  announcement_deleted: 'Annonce supprimée',
  user_approved: 'Utilisateur approuvé',
  user_rejected: 'Utilisateur refusé',
  profile_update: 'Profil mis à jour',
  pdf_report_sent: 'Rapport PDF envoyé',
  auto_pdf_sent: 'Rapport PDF automatique envoyé',
  eos_warning_acknowledged: "Avertissement fin de service acquitté",
  charter_accepted: "Charte d'utilisation signée",
  rgpd_export: 'Export de données personnelles',
  rgpd_rectification: 'Coordonnées rectifiées',
  rgpd_purge_pieces_jointes: 'Pièces jointes supprimées (RGPD)',
  pin_created: 'Code personnel créé/modifié',
  password_changed: 'Mot de passe modifié',
  password_reset: 'Mot de passe réinitialisé',
  reset_user_pin: "Code personnel réinitialisé par un administrateur",
  consigne_created: 'Consigne créée',
  consigne_updated: 'Consigne modifiée',
  consigne_deleted: 'Consigne supprimée',
  directory_contact_created: 'Contact annuaire créé',
  directory_contact_updated: 'Contact annuaire modifié',
  directory_contact_deleted: 'Contact annuaire supprimé',
  site_created: 'Site créé',
  site_updated: 'Site modifié',
  site_deleted: 'Site supprimé',
  site_toggled: 'Statut du site modifié',
  user_created: 'Utilisateur créé',
  user_updated: 'Utilisateur modifié',
  user_deleted: 'Utilisateur supprimé',
  user_toggled: 'Statut utilisateur modifié',
  role_created: 'Rôle créé',
  role_updated: 'Rôle modifié',
  role_deleted: 'Rôle supprimé',
  calendar_event_created: 'Événement calendrier créé',
  calendar_event_updated: 'Événement calendrier modifié',
  calendar_event_deleted: 'Événement calendrier supprimé',
  reporting_updated: 'Reporting quotidien mis à jour',
  trombinoscope_updated: "Fiche organigramme mise à jour",
};

const getActionModule = (actionType) => {
  const t = actionType || '';
  let key = 'autre';
  if (t.startsWith('event_') || t === 'data_access' || t.startsWith('key_')) key = 'main_courante';
  else if (t.startsWith('consigne_')) key = 'consignes';
  else if (t.startsWith('directory_contact_')) key = 'annuaire';
  else if (t.startsWith('trombinoscope_')) key = 'organigramme';
  else if (t.startsWith('site_')) key = 'sites';
  else if (t.startsWith('user_') || t === 'reset_user_pin') key = 'utilisateurs';
  else if (t.startsWith('role_')) key = 'permissions';
  else if (t.startsWith('calendar_event_')) key = 'calendrier';
  else if (t.startsWith('announcement_')) key = 'messagerie';
  else if (t.startsWith('agent_report_') || t.startsWith('agent_control_')) key = 'rapports';
  else if (t.startsWith('reporting_') || t === 'pdf_report_sent' || t === 'auto_pdf_sent') key = 'reporting';
  else if (['eos_warning_acknowledged', 'charter_accepted', 'rgpd_export', 'rgpd_rectification', 'pin_created', 'password_changed', 'password_reset', 'profile_update'].includes(t)) key = 'compte';
  return { key, ...MODULES[key] };
};

const getActionLabel = (actionType) => ACTION_LABELS[actionType] || actionType;

const ActivityHistory = ({ user, onLogout }) => {
  const [logs, setLogs] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedModule, setSelectedModule] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [selectedSite]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [logsRes, sitesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/activity-logs${selectedSite !== 'all' ? `?site_id=${selectedSite}` : ''}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/sites`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (logsRes.ok) {
        const data = await logsRes.json();
        setLogs(data);
      }

      if (sitesRes.ok) {
        const data = await sitesRes.json();
        setSites(data);
        // Un non-admin n'a pas la possibilité de choisir "Tous les sites" :
        // son site (unique, ou celui choisi à la connexion) est sélectionné directement.
        if (user?.role !== 'admin' && data.length > 0) {
          setSelectedSite(prev => (prev === 'all' ? data[0].id : prev));
        }
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = selectedModule === 'all' ? logs : logs.filter((log) => getActionModule(log.action_type).key === selectedModule);

  if (user.role !== 'admin' && user.role !== 'chef_poste') {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-slate-300">Accès réservé aux administrateurs et chefs de poste</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 animate-fade-in" data-testid="activity-history">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Historique d'Activité
          </h1>
          <p className="text-gray-600 dark:text-slate-300">
            {user.role === 'admin' ? 'Tous les sites' : 'Votre site'}
          </p>
        </div>

        <Card className="glass-effect">
          <CardHeader>
            <CardTitle>Filtrer</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Site</p>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger>
                  <SelectValue placeholder="Tous les sites" />
                </SelectTrigger>
                <SelectContent>
                  {user?.role === 'admin' && <SelectItem value="all">Tous les sites</SelectItem>}
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Partie de l'application</p>
              <Select value={selectedModule} onValueChange={setSelectedModule}>
                <SelectTrigger data-testid="activity-module-filter">
                  <SelectValue placeholder="Toutes les parties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les parties</SelectItem>
                  {Object.entries(MODULES).map(([key, mod]) => (
                    <SelectItem key={key} value={key}>{mod.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : filteredLogs.length === 0 ? (
            <Card className="glass-effect">
              <CardContent className="py-12 text-center">
                <Activity className="h-12 w-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-slate-300">Aucune activité enregistrée</p>
              </CardContent>
            </Card>
          ) : (
            filteredLogs.map((log) => {
              const mod = getActionModule(log.action_type);
              return (
              <Card key={log.id} className="card-hover glass-effect">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge className="text-white border-transparent" style={{ backgroundColor: mod.color }}>
                          {mod.label}
                        </Badge>
                        <span className="text-xs text-slate-500 dark:text-slate-400">{getActionLabel(log.action_type)}</span>
                        <span className="text-sm font-medium text-gray-700 dark:text-slate-200">{log.user_name}</span>
                      </div>
                      <p className="text-gray-700 dark:text-slate-200 mb-2">{log.action_description}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-slate-400">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(log.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                        </div>
                        {log.site_id && (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {sites.find(s => s.id === log.site_id)?.name || 'Site inconnu'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ActivityHistory;
